import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { DialogCreaDoppiaMaterialitaComponent } from 'src/app/page/analisi-materialita/doppia-materialita/dialog/dialog-crea-doppia-materialita/dialog-crea-doppia-materialita.component';
import { DialogCreaMaterialitaComponent } from 'src/app/page/analisi-materialita/materialita/dialog/dialog-crea-materialita/dialog-crea-materialita.component';
import { DialogCreaBilancioSostenibilitaComponent } from 'src/app/page/bilancio-sostebilita/dialog/dialog-crea-bilancio-sostenibilita/dialog-crea-bilancio-sostenibilita.component';
import { DialogCreaPianoSostenibilitaComponent } from 'src/app/page/piano-sostenibilita/dialog/dialog-crea-piano-sostenibilita/dialog-crea-piano-sostenibilita.component';
import { DialogCreaPolicySostenibilitaComponent } from 'src/app/page/policy-sostenibilita/dialog/dialog-crea-policy-sostenibilita/dialog-crea-policy-sostenibilita.component';
import { DialogCreaSintesiSostenibilitaComponent } from 'src/app/page/sintesi-sostenibilita/dialog/dialog-crea-sintesi-sostenibilita/dialog-crea-sintesi-sostenibilita.component';
import {
  AutovalutazioneCompilata,
  AutovalutazioniService,
} from 'src/app/services/autovalutazioni/autovalutazioni.service';
import { BilancioService } from 'src/app/services/bilancio/bilancio.service';
import { DoppiaMaterialitaService } from 'src/app/services/doppia-materialita/doppia-materialita.service';
import { MaterialitaService } from 'src/app/services/materialita/materialita.service';
import { PianoService } from 'src/app/services/piano/piano.service';
import { PolicyService } from 'src/app/services/policy/policy.service';
import { Settore, SottoSettore } from 'src/app/services/settori/settori.service';
import { SintesiService } from 'src/app/services/sintesi/sintesi.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay.component';

type TipoDescrizione =
  | 'idMaterialita'
  | 'idDoppiaMaterialita'
  | 'idSintesiSostenibilita'
  | 'idAutovalutazioneCompilata'
  | 'idPolicySostenibilita'
  | 'idPianoSostenibilita'
  | 'idBilancioSostenibilita';

type TipoMoodulo =
  | 'AUTOVALUTAZIONE'
  | 'MATERIALITA'
  | 'SINTESI'
  | 'BILANCIO'
  | 'POLICY'
  | 'PIANO'
  | 'DOPPIA_MATERIALITA'
  | 'REPORT_VSME'
  | undefined;

export interface ModuliInclusi {
  idMaterialita?: string;
  idDoppiaMaterialita?: string;
  idSintesiSostenibilita?: string;
  idAutovalutazioneCompilata?: string;
  idPolicySostenibilita?: string;
  idPianoSostenibilita?: string;
  idBilancioSostenibilita?: string;
}

export interface ModuliPubblicati {
  id: string;
  nome: string;
  anno: string;
}

@Component({
  selector: 'app-inclusione-moduli',
  standalone: false,

  templateUrl: './inclusione-moduli.component.html',
  styleUrl: './inclusione-moduli.component.scss',
})
export class InclusioneModuliComponent implements OnChanges {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  constructor(
    private readonly _utilityService: UtilityService,
    private readonly _dialog: MatDialog,
    private readonly _sintesiService: SintesiService,
    private readonly _bilancioService: BilancioService,
    private readonly _autovalutazioniService: AutovalutazioniService,
    private readonly _policyService: PolicyService,
    private readonly _pianoService: PianoService,
    private readonly _materialitaService: MaterialitaService,
    private readonly _doppiaMaterialitaService: DoppiaMaterialitaService,
    private readonly _cdRef: ChangeDetectorRef,
  ) {}

  private _idModuiliInclusi: ModuliInclusi | undefined;

  @Input()
  get idModuiliInclusi(): ModuliInclusi | undefined {
    return this._idModuiliInclusi;
  }

  set idModuiliInclusi(value: ModuliInclusi | undefined) {
    this._idModuiliInclusi = value;
  }

  private _settore?: Settore;

  @Input()
  get settore(): Settore | undefined {
    return this._settore;
  }

  set settore(value: Settore | undefined) {
    this._settore = value;
  }

  private _sottoSettore: SottoSettore[] = [];

  @Input()
  get sottoSettore(): SottoSettore[] {
    return this._sottoSettore;
  }

  set sottoSettore(value: SottoSettore[] | undefined) {
    this._sottoSettore = value ?? []; // fallback a array vuoto se undefined
  }

  private _isUtenteGo: boolean = false;

  @Input()
  get isUtenteGo(): boolean {
    return this._isUtenteGo;
  }

  set isUtenteGo(value: boolean) {
    this._isUtenteGo = value;
  }

  private _isSoglieSuperate: boolean = false;

  @Input()
  get isSoglieSuperate(): boolean {
    return this._isSoglieSuperate;
  }

  set isSoglieSuperate(value: boolean) {
    this._isSoglieSuperate = value;
  }
  private _idModulo: string | undefined;

  @Input()
  get idModulo(): string | undefined {
    return this._idModulo;
  }

  set idModulo(value: string | undefined) {
    this._idModulo = value;
  }

  private _annoModulo: string | undefined;

  @Input()
  get annoModulo(): string | undefined {
    return this._annoModulo;
  }

  set annoModulo(value: string | undefined) {
    this._annoModulo = value;
  }

  private _tipoModulo: TipoMoodulo;

  @Input()
  set tipoModulo(tipo: TipoMoodulo) {
    this._tipoModulo = tipo;
  }

  get tipoModulo() {
    return this._tipoModulo;
  }

  public formInclusioneModuli = new FormGroup({
    idMaterialita: new FormControl<string | ModuliPubblicati>('', {
      nonNullable: true,
      validators: [],
    }),
    idDoppiaMaterialita: new FormControl<string | ModuliPubblicati>('', {
      nonNullable: true,
      validators: [],
    }),
    idSintesiSostenibilita: new FormControl<string | ModuliPubblicati>('', {
      nonNullable: true,
      validators: [],
    }),
    idAutovalutazioneCompilata: new FormControl<string | AutovalutazioneCompilata>('', {
      nonNullable: true,
      validators: [],
    }),

    idPolicySostenibilita: new FormControl<string | ModuliPubblicati>('', {
      nonNullable: true,
      validators: [],
    }),

    idPianoSostenibilita: new FormControl<string | ModuliPubblicati>('', {
      nonNullable: true,
      validators: [],
    }),
    idBilancioSostenibilita: new FormControl<string | ModuliPubblicati>('', {
      nonNullable: true,
      validators: [],
    }),
  });

  public arrayAnalisiMaterialita: false | ModuliPubblicati[] = [];
  public isMaterialitaSelezionata: boolean = false;
  public arrayDoppiaMaterialita: false | ModuliPubblicati[] = [];
  public arraySintesiSostenibilita: false | ModuliPubblicati[] = [];
  public arrayBilancioSostenibilita: false | ModuliPubblicati[] = [];
  public arrayAnalisiAutovalutazione: false | ModuliPubblicati[] = [];
  public arrayPolicySostenibilita: false | ModuliPubblicati[] = [];
  public arrayPianoSostenibilita: false | ModuliPubblicati[] = [];
  public selectedAnalisiMaterialita: string[] = [];
  public guidaDescrizione: string | undefined = undefined;
  public isLoadingSpinner: boolean = true;

  private readonly descrizioni: Record<TipoDescrizione, [string, string]> = {
    idAutovalutazioneCompilata: ['Assessment di Sostenibilità', 'Assessment non selezionato'],
    idMaterialita: ['Analisi Materialità', 'Materialità non selezionata'],
    idDoppiaMaterialita: ['Doppia Materialità', 'Doppia Materialità non selezionata'],
    idSintesiSostenibilita: ['Sintesi di Sostenibilità', 'Sintesi non selezionata'],
    idPolicySostenibilita: ['Policy di Sostenibilità', 'Policy non selezionata'],
    idPianoSostenibilita: ['Piano di Sostenibilità', 'Piano non selezionato'],
    idBilancioSostenibilita: ['Report di Sostenibilità', 'Report non selezionato'],
  };

  ngOnChanges(changes: SimpleChanges): void {
    // Settore
    if (changes['settore']?.currentValue) {
      if (this.settore) {
        firstValueFrom(this.getForkDatiIniziali(this.settore.id));
      }
    }

    // SottoSettore change
    if (changes['sottoSettore'] && this.sottoSettore.length) {
      if (this.settore) {
        firstValueFrom(this.getForkDatiIniziali(this.settore.id));
      }
    }

    // idModulo change
    if (changes['idModulo']?.currentValue) {
      this.formInclusioneModuli.disable();
    }

    // annoModulo change
    if (changes['annoModulo']?.currentValue) {
      this._infoIfSoglieSuperate();
    }

    // isSoglieSuperate change   DA RIVEDERE I CONTROLLI
    if (changes['isSoglieSuperate']?.currentValue !== undefined) {
      this._ctrValidatorIfSoglie();
      this._infoIfSoglieSuperate();
    }
  }

  /**
   * Inserisco o rimuovo i validatori sul control della materialita nel caso le soglie sono attive oppure no
   * @returns
   */
  private _ctrValidatorIfSoglie() {
    const materialitaControl = this.formInclusioneModuli.get('idMaterialita');
    if (!materialitaControl) return;

    const materialitaIsRequired = materialitaControl?.hasValidator(Validators.required);

    if (this.isSoglieSuperate && !materialitaIsRequired) {
      materialitaControl.addValidators(Validators.required);
    } else if (!this.isSoglieSuperate && materialitaIsRequired) {
      materialitaControl.removeValidators(Validators.required);
    }

    materialitaControl.updateValueAndValidity();
  }

  /**
   * Valorizzo la descrizione con il messaggio corretto in base al comportameto delle solgie
   * e se la materialità è stat selezionata oppure no
   * @returns
   */
  private _infoIfSoglieSuperate() {
    this.guidaDescrizione = undefined;
    const materialitaControl = this.formInclusioneModuli.get('idMaterialita');
    if (!materialitaControl) return;

    const hasValue = !!materialitaControl.getRawValue();

    if (hasValue) {
      this.cambiaInfoBoxDesc();
    } else if (!hasValue && this.isSoglieSuperate) {
      switch (this.tipoModulo) {
        case 'BILANCIO':
          this.guidaDescrizione = "È obbligatorio indicare l'Analisi di Materialità";
          break;
        case 'SINTESI':
          this.guidaDescrizione = "È consigliato indicare l'Analisi di Materialità.";
          break;
      }
    }
  }

  compareGenericoById(a: ModuliPubblicati, b: ModuliPubblicati): boolean {
    return a.id === b.id;
  }

  compareAnalisiAutovalutazione(a: AutovalutazioneCompilata, b: AutovalutazioneCompilata): boolean {
    return a.id === b.id;
  }

  public mostraModuli(modulo: TipoMoodulo): boolean {
    // Se il modulo da includere è uguale al tipo di modulo corrente, non mostrare il modulo
    if (modulo === this.tipoModulo) {
      return false;
    }

    switch (modulo) {
      case 'DOPPIA_MATERIALITA':
        return this.tipoModulo === 'BILANCIO'; // Mostro la doppia materialità solo nel bilancio
      case 'BILANCIO':
        return this.tipoModulo !== 'SINTESI'; // Mostro il bilancio in tutti i moduli tranne la sintesi
      default:
        return true; // Mostro tutti gli altri moduli in tutti i casi
    }
  }

  /**
   * Mostra la stringa di descrizone
   * @param tipo tipo modulo
   * @param array array dei modulo, tipizzato unknown -> come se fosse any ma con controlli sul tipo
   * @returns descrizoine
   */
  mostraDescrizione(tipo: TipoDescrizione): string {
    const [presente, assente] = this.descrizioni[tipo];

    const idModuloSelezionato = this.formInclusioneModuli.get(tipo)?.getRawValue();

    if (this.idModulo && !idModuloSelezionato) {
      return assente;
    } else {
      return presente;
    }
  }

  get descrizioneAssessment(): string {
    return this.mostraDescrizione('idAutovalutazioneCompilata');
  }

  get descrizioneMaterialita(): string {
    return this.mostraDescrizione('idMaterialita');
  }

  get descrizioneDoppiaMaterialita(): string {
    return this.mostraDescrizione('idDoppiaMaterialita');
  }

  get descrizioneSintesi(): string {
    return this.mostraDescrizione('idSintesiSostenibilita');
  }

  get descrizionePolicy(): string {
    return this.mostraDescrizione('idPolicySostenibilita');
  }

  get descrizionePiano(): string {
    return this.mostraDescrizione('idPianoSostenibilita');
  }
  get descrizioneBilancio(): string {
    return this.mostraDescrizione('idBilancioSostenibilita');
  }

  /**
   * Valorizzo la descrizione come informaizone nel caso la matarialià è più vecchia di 2 anni
   * @returns
   */
  public cambiaInfoBoxDesc() {
    this.guidaDescrizione = undefined;
    const annoMaterialita = (
      this.formInclusioneModuli.get('idMaterialita')?.value as ModuliPubblicati
    )?.anno;

    if (!annoMaterialita || !this.annoModulo) return;

    /* Controllo se anno della materialità ha anzianità >= 2 anni   */
    if (this.annoModulo) {
      const annoModulo = parseInt(this.annoModulo, 10);
      const annoMaterialitaNum: number = parseInt(annoMaterialita ?? '', 10);

      const diffAnno: number = annoModulo - annoMaterialitaNum;
      if (diffAnno >= 2) {
        this.guidaDescrizione = "Sono passati 2 anni dall'Analisi di Materialità selezionata";
      }
    }
  }

  /**
   * Se seleziono una materialità oppure una doppia materialità, cambio descrizione e mando alert se sono in bilancio
   * @param $event
   * @param titolo
   */
  onSelectionChangeMaterialita($event: MatSelectChange, titolo: string) {
    this.guidaDescrizione = undefined;
    this.cambiaInfoBoxDesc();
    if (!this.isMaterialitaSelezionata && this.tipoModulo === 'BILANCIO') {
      this._utilityService.openDialog({
        titolo: `Inclusione ${titolo}`,
        descrizione: `Collegando la tua <strong>${titolo}</strong> al Report di Sostenibilità, le <strong>tematiche materiali</strong> <br>saranno <strong>preselezionate</strong> nello step <strong>"Tematiche"</strong> e sarà quindi <strong>consigliato</strong> trattarle.`,
        bottoni: [{ nome_btn: 'Ok' }],
        fontWeight: '500',
      });
    }

    this.isMaterialitaSelezionata = true;
  }

  // Gestione cancellazione in mat select, disabilito il bottone per le seguenti condizioni
  hasValue(formControlName: string) {
    if (this.formInclusioneModuli.get(formControlName)?.value) {
      if (formControlName === 'idDoppiaMaterialita') {
        this.formInclusioneModuli.get('idMaterialita')?.disable();
      }

      if (formControlName === 'idMaterialita') {
        this.formInclusioneModuli.get('idDoppiaMaterialita')?.disable();
      }
    }

    if (
      this.idModulo ||
      !this.formInclusioneModuli.get(formControlName)?.value ||
      this.formInclusioneModuli.get(formControlName)?.status === 'DISABLED'
    ) {
      return true;
    }
    return false;
  }

  /**
   * Mi pulisce il campo di select, quindi il form control associato ad esso
   */
  clearInput(formControlName: string, event?: Event): void {
    const field = this.formInclusioneModuli.get(formControlName);

    if (formControlName === 'idMaterialita') {
      this.isMaterialitaSelezionata = false;
      this.formInclusioneModuli.get('idDoppiaMaterialita')?.enable();
    }

    if (formControlName === 'idDoppiaMaterialita') {
      this.isMaterialitaSelezionata = false;
      this.formInclusioneModuli.get('idMaterialita')?.enable();
    }

    if (!field?.disabled) {
      field?.reset();
      field?.markAsDirty();
      field?.markAsTouched();
    }
    if (event) event.stopPropagation();

    if (!this.formInclusioneModuli.get('idMaterialita')?.value) {
      this._infoIfSoglieSuperate();
    }
  }

  /**
   * Popola i valori dei controlli del form `formInclusioneModuli`
   * utilizzando i dati presenti nelle varie liste di moduli (`arrayAnalisiMaterialita`,
   * `arrayDoppiaMaterialita`, ecc.) in base agli identificativi memorizzati
   * nell'oggetto `_idModuiliInclusi`.
   *
   * Per ogni campo definito nel mapping:
   * - Recupera l'`id` corrispondente da `_idModuiliInclusi`.
   * - Cerca nell'array associato il modulo con quell'`id`.
   * - Imposta il valore del `FormControl` corrispondente con l'oggetto trovato
   *   oppure con una stringa vuota se non viene trovato nulla.
   *
   * @private
   * @remarks
   * - Il tipo `ModuliInclusiKeys` garantisce che `idKey` sia sempre una chiave valida di `ModuliInclusi`.
   * - Ogni elemento del mapping associa il nome del `FormControl` (`control`),
   *   l'array dei moduli da cui cercare (`array`) e la chiave di riferimento nell'oggetto `_idModuiliInclusi` (`idKey`).
   *
   * @example
   *  Esempio di popolamento del form
   *  Se _idModuiliInclusi.idMaterialita = '123' e nell'arrayAnalisiMaterialita
   *  esiste un modulo con id = '123', il controllo 'idMaterialita'
   *  conterrà quell'oggetto ModuliPubblicati.
   */
  private _setDatiForm() {
    type ModuliInclusiKeys = keyof ModuliInclusi;

    const mapping: {
      control: string;
      array: ModuliPubblicati[] | undefined;
      idKey: ModuliInclusiKeys;
    }[] = [
      {
        control: 'idMaterialita',
        array: this.arrayAnalisiMaterialita as ModuliPubblicati[],
        idKey: 'idMaterialita',
      },
      {
        control: 'idDoppiaMaterialita',
        array: this.arrayDoppiaMaterialita as ModuliPubblicati[],
        idKey: 'idDoppiaMaterialita',
      },
      {
        control: 'idSintesiSostenibilita',
        array: this.arraySintesiSostenibilita as ModuliPubblicati[],
        idKey: 'idSintesiSostenibilita',
      },
      {
        control: 'idAutovalutazioneCompilata',
        array: this.arrayAnalisiAutovalutazione as ModuliPubblicati[],
        idKey: 'idAutovalutazioneCompilata',
      },
      {
        control: 'idPolicySostenibilita',
        array: this.arrayPolicySostenibilita as ModuliPubblicati[],
        idKey: 'idPolicySostenibilita',
      },
      {
        control: 'idPianoSostenibilita',
        array: this.arrayPianoSostenibilita as ModuliPubblicati[],
        idKey: 'idPianoSostenibilita',
      },
      {
        control: 'idBilancioSostenibilita',
        array: this.arrayBilancioSostenibilita as ModuliPubblicati[],
        idKey: 'idBilancioSostenibilita',
      },
    ];

    if (
      this._idModuiliInclusi &&
      Object.values(this._idModuiliInclusi).some((v) => v !== undefined)
    ) {
      mapping.forEach(({ control, array, idKey }) => {
        if (array) {
          const idValue = this._idModuiliInclusi?.[idKey];
          this.formInclusioneModuli
            .get(control)
            ?.setValue(array.find((form) => form.id === idValue) ?? '');
        }
      });
    }
  }

  public getForkDatiIniziali(idSettore: string): Observable<any> {
    this.isLoadingSpinner = true;

    const sottoSettori = this._getSottoSettori();

    if (idSettore) {
      return forkJoin({
        autovalutazione: this._getAutovalutazioniObservable(idSettore, sottoSettori),
        analisiMaterialita: this._getAnalisiMaterialitaObservable(idSettore, sottoSettori),
        doppiaMaterialita: this._getDoppiaMaterialitaObservable(idSettore, sottoSettori),
        sintesiSostenibilita: this._getSintesiSostenibilitaObservable(idSettore, sottoSettori),
        policySostenibilita: this._getPolicySostenibilitaObservable(idSettore, sottoSettori),
        pianoSostenibilita: this._getPianoSostenibilitaObservable(idSettore, sottoSettori),
        bilancioSostenibilita: this._getBilanciPubblicatiObservable(idSettore, sottoSettori),
      }).pipe(
        switchMap((result) => {
          this.arrayAnalisiAutovalutazione = result.autovalutazione;
          this.arrayAnalisiMaterialita = result.analisiMaterialita;
          this.arrayDoppiaMaterialita = result.doppiaMaterialita;
          this.arraySintesiSostenibilita = result.sintesiSostenibilita;
          this.arrayPolicySostenibilita = result.policySostenibilita;
          this.arrayPianoSostenibilita = result.pianoSostenibilita;
          this.arrayBilancioSostenibilita = result.bilancioSostenibilita;

          if (
            this._idModuiliInclusi &&
            Object.values(this._idModuiliInclusi).some((v) => v !== undefined)
          ) {
            this._setDatiForm();
          }
          this.isLoadingSpinner = false;
          this.spinnerOver.hide();
          return of(true);
        }),
        catchError((err) => {
          console.error(err);
          this.spinnerOver.hide();
          return of(false);
        }),
      );
    } else {
      console.error('Nessun settore selezionato');
      this.spinnerOver.hide();
      return of(false);
    }
  }

  private _alertSettoreNonSelezionato() {
    this._utilityService.openSnackBar('Selezionare prima un settore', '', {
      duration: 2000,
      panelClass: ['red-snackbar'],
    });
  }

  /**
   * Apre un dialog generica e aggiorna i dati al termine della sua chiusura.
   *
   * Metodo generico per aprire le dialog di creazione (es. materialità, doppia materialità, sintesi, ecc.).
   * Una volta che la dialog viene chiusa, viene eseguita la funzione `refreshObservable` passa nei parametri.
   * Passo l'`idSettore` selezionato, al fine di aggiornare i dati della select sulla quale aziono la creazione del nuovo modulo.
   *
   * @param dialogComponent - Il componente da visualizzare nel dialog (es. `DialogCreaMaterialitaComponent`).
   * @param refreshObservable - Una funzione che, dato l'id del settore, ritorna un `Observable` da eseguire per aggiornare i dati.
   *
   * Se `idSettore` non è presente, viene mostrato un avviso tramite `_alertSettoreNonSelezionato()`
   * e il dialog non viene aperto.
   */
  private openDialogAndRefresh(
    dialogComponent: any,
    refreshObservable: (idSettore: string, idSottoSettori: string[]) => Observable<any>,
    onSuccess: (data: any) => void, // Ritorno la lista con i moduli rifreshati
  ): void {
    const idSettore = this.settore?.id;
    const sottoSettori = this._getSottoSettori();

    if (!idSettore) {
      this._alertSettoreNonSelezionato();
      return;
    }

    const dialogRef = this._dialog.open(dialogComponent, {
      data: {},
      panelClass: 'dialog-container',
      disableClose: false,
      width: '95%',
      height: '99%',
      maxHeight: '99%',
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(
        // switchMap annulla eventuali chiamate precedenti se ne arriva una nuova
        switchMap(() => refreshObservable(idSettore, sottoSettori)),
      )
      .subscribe({
        // quando ho i dati, eseguo l'istruzione passata dal metodo padre, quindi valorizzare
        // la lista con il risulattao della subscribne
        next: (res) => onSuccess(res),
        error: (err) => console.error(err),
      });
  }

  /**
   * Metodo per creare una nuova materialità
   *
   * Il metodo `_getAnalisiMaterialitaObservable` viene passato con `.bind(this)` per mantenere
   * il corretto contesto `this` all'interno della callback (in questo caso tutti
   * i this che sono presenti all'interno del metodo _getAnalisiMaterialitaObservable).
   * Questo è necessario perché il metodo viene passato come parametro a una funzione esterna (`openDialogAndRefresh`) che lo eseguirà
   * in un contesto diverso. Se evito il .bind(this) tutti gli usage dei this.qualcosa dentro al metodo mi darà undefined, perchè perde il collegamento
   *
   */
  addMaterialita(): void {
    this.openDialogAndRefresh(
      DialogCreaMaterialitaComponent,
      this._getAnalisiMaterialitaObservable.bind(this),
      (listaModuliUpdated) => {
        this.arrayAnalisiMaterialita = listaModuliUpdated;
      },
    );
  }

  addDoppiaMaterialita(): void {
    this.openDialogAndRefresh(
      DialogCreaDoppiaMaterialitaComponent,
      this._getDoppiaMaterialitaObservable.bind(this),
      (listaModuliUpdated) => {
        this.arrayDoppiaMaterialita = listaModuliUpdated;
      },
    );
  }

  addSintesi(): void {
    this.openDialogAndRefresh(
      DialogCreaSintesiSostenibilitaComponent,
      this._getSintesiSostenibilitaObservable.bind(this),
      (listaModuliUpdated) => {
        this.arraySintesiSostenibilita = listaModuliUpdated;
      },
    );
  }

  addPolicy() {
    this.openDialogAndRefresh(
      DialogCreaPolicySostenibilitaComponent,
      this._getPolicySostenibilitaObservable.bind(this),
      (listaModuliUpdated) => {
        this.arrayPolicySostenibilita = listaModuliUpdated;
      },
    );
  }

  addPiano() {
    this.openDialogAndRefresh(
      DialogCreaPianoSostenibilitaComponent,
      this._getPianoSostenibilitaObservable.bind(this),
      (listaModuliUpdated) => {
        this.arrayPianoSostenibilita = listaModuliUpdated;
      },
    );
  }

  addBilancio() {
    this.openDialogAndRefresh(
      DialogCreaBilancioSostenibilitaComponent,
      this._getBilanciPubblicatiObservable.bind(this),
      (listaModuliUpdated) => {
        this.arrayBilancioSostenibilita = listaModuliUpdated;
      },
    );
  }

  private _getSottoSettori(): string[] {
    return (this.sottoSettore || [])
      .map((s) => s.id)
      .filter((id): id is string => id !== undefined);
  }

  /**
   * Metodo generico per recuperare e mappare dati di moduli pubblicati da un servizio.
   *
   * @template ModuliPubblicati - Tipo degli elementi restituiti dal servizio.
   * @param {(idSettore?: string, idSottoSettori: string[]) => Observable<ModuliPubblicati[]>} metodoServiceGetModuli
   *   Funzione del servizio che restituisce un Observable con un array di elementi di tipo ModuliPubblicati.
   * @param {string} [idSettore] - id del settore per filtrare i dati.
   * @param {string[]} [idSottoSettori] - id dei sotto-settori per filtrare i dati.
   * @param {string} [labelPrefix] - Prefisso utlizzato nella proprietà "nome" degli elementi mappati oppure come identificativo.
   * @param {string} [idKey='id'] - Nome della proprietà dell'oggetto ModuliPubblicati da usare come identificativo (default 'id').
   * @param {string} [annoKey='anno'] - Nome della proprietà dell'oggetto ModuliPubblicati da usare come anno (default 'anno').
   *
   * @returns {Observable<Array<{ id: any; nome: string; anno: any }> | false>}
   *   Observable che emette un array di oggetti mappati con proprietà `id`, `nome` e `anno`,
   *   oppure `false` in caso di errore, dopo aver aperto un dialog di avviso.
   */
  private _getDatiModuliGenerico<ModuliPubblicati>(
    metodoServiceGetModuli: (
      idSettore: string,
      idSottoSettori: string[],
    ) => Observable<ModuliPubblicati[]>,
    idSettore: string,
    idSottoSettori: string[],
    labelPrefix: string,
    idKey: string = 'id',
    annoKey: string = 'anno',
  ): Observable<{ id: any; nome: string; anno: any }[] | false> {
    return metodoServiceGetModuli(idSettore, idSottoSettori).pipe(
      map((results: ModuliPubblicati[]) =>
        results.map((item: any) => ({
          id: item[idKey],
          nome: item[labelPrefix] ? item[labelPrefix] : `${labelPrefix ?? 'Elemento'}`,
          anno: item[annoKey],
        })),
      ),
      catchError((err) => {
        this._utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: `Errore reperimento ${labelPrefix?.toLowerCase() ?? 'dati'} pubblicate`,
          bottoni: [{ nome_btn: 'Ok' }],
        });
        return of(false as const);
      }),
    );
  }

  private _getAnalisiMaterialitaObservable(idSettore: string, idSottoSettori: string[]) {
    return this._getDatiModuliGenerico(
      this._materialitaService.getMaterialitaPubblicate.bind(this._materialitaService),
      idSettore,
      idSottoSettori,
      'Analisi Materialità',
      'id',
      'anno',
    );
  }

  private _getDoppiaMaterialitaObservable(idSettore: string, idSottoSettori: string[]) {
    return this._getDatiModuliGenerico(
      this._doppiaMaterialitaService.getDoppiaMaterialitaPubblicate.bind(
        this._doppiaMaterialitaService,
      ),
      idSettore,
      idSottoSettori,
      'Doppia Materialità',
      'id',
      'anno',
    );
  }

  private _getSintesiSostenibilitaObservable(idSettore: string, idSottoSettori: string[]) {
    return this._getDatiModuliGenerico(
      this._sintesiService.getSintesiPubblicate.bind(this._sintesiService),
      idSettore,
      idSottoSettori,
      'Sintesi Sostenibilita',
      'id',
      'anno',
    );
  }

  private _getPolicySostenibilitaObservable(idSettore: string, idSottoSettori: string[]) {
    return this._getDatiModuliGenerico(
      this._policyService.getPolicyPubblicate.bind(this._policyService),
      idSettore,
      idSottoSettori,
      'Policy Sostenibilita',
      'id',
      'anno',
    );
  }

  private _getPianoSostenibilitaObservable(idSettore: string, idSottoSettori: string[]) {
    return this._getDatiModuliGenerico(
      this._pianoService.getPianiPubblicate.bind(this._pianoService),
      idSettore,
      idSottoSettori,
      'Piano Sostenibilita',
      'id',
      'anno',
    );
  }

  private _getBilanciPubblicatiObservable(idSettore: string, idSottoSettori: string[]) {
    return this._getDatiModuliGenerico(
      this._bilancioService.getBilanciPubblicati.bind(this._bilancioService),
      idSettore,
      idSottoSettori,
      'Report Sostenibilita',
      'id',
      'anno',
    );
  }

  /**
   * Metodo che mi richiama la lista delle autovalutazioni compilate
   */
  private _getAutovalutazioniObservable(
    idSettore: string,
    idSottoSettori: string[],
  ): Observable<any> {
    return this._getDatiModuliGenerico(
      this._autovalutazioniService.getAutovalutazioniCompilatePerSettore.bind(
        this._autovalutazioniService,
      ),
      idSettore,
      idSottoSettori,
      'titolo',
      'id',
      'esercizio',
    );
  }
}
